import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import communityImage from '../assets/image2.jpg';
import { 
  ArrowDown, Sparkles, Globe, Lock, Zap, Users, Star,
  MessageCircle, Video, Users2, Hash, HeartHandshake 
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

// Background images
const gradientBg = "https://ik.imagekit.io/confesz/main11.avif";

function Home() {
  const canvasRef = useRef(null);
  const { t } = useTranslation();

  // Particle animation effect
  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animationFrameId;
    
    const setCanvasSize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    setCanvasSize();
    
    const particles = [];
    const particleCount = 150;
    
    for (let i = 0; i < particleCount; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        radius: Math.random() * 2 + 1,
        speedX: (Math.random() - 0.5) * 0.5,
        speedY: (Math.random() - 0.5) * 0.5,
        color: `rgba(255, 255, 0, ${Math.random() * 0.6 + 0.2})`,
        glow: Math.random() * 0.8 + 0.3
      });
    }
    
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      particles.forEach(particle => {
        particle.x += particle.speedX;
        particle.y += particle.speedY;
        
        if (particle.x < 0) particle.x = canvas.width;
        if (particle.x > canvas.width) particle.x = 0;
        if (particle.y < 0) particle.y = canvas.height;
        if (particle.y > canvas.height) particle.y = 0;
        
        ctx.shadowBlur = 15 * particle.glow;
        ctx.shadowColor = '#FFFF00';
        
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
        ctx.fillStyle = particle.color;
        ctx.fill();
      });
      
      ctx.shadowBlur = 0;
      
      animationFrameId = requestAnimationFrame(animate);
    };
    
    animate();
    
    window.addEventListener('resize', setCanvasSize);
    
    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', setCanvasSize);
    };
  }, []);

  const handleDownload = (url) => {
    window.open(url, '_blank', 'noopener noreferrer');
  };

  return (
    <div className="relative bg-black text-white overflow-x-hidden">
      <canvas 
        ref={canvasRef} 
        className="fixed inset-0 pointer-events-none z-10 opacity-60" 
      />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={gradientBg}
            alt="Background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/60" />
        </div>

        <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/20 via-transparent to-yellow-500/20" />
        
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-yellow-500 rounded-full mix-blend-screen filter blur-3xl opacity-30 animate-blob" />
          <div className="absolute top-40 right-10 w-72 h-72 bg-amber-500 rounded-full mix-blend-screen filter blur-3xl opacity-30 animate-blob animation-delay-2000" />
          <div className="absolute bottom-20 left-1/2 w-72 h-72 bg-yellow-600 rounded-full mix-blend-screen filter blur-3xl opacity-30 animate-blob animation-delay-4000" />
        </div>
        
        <div className="relative z-20 text-center px-4 max-w-6xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="inline-flex items-center gap-2 bg-yellow-500/20 backdrop-blur-lg px-4 py-2 rounded-full mb-8 border border-yellow-500/40"
          >
            <Sparkles className="w-4 h-4 text-yellow-400" />
            <span className="text-sm font-medium text-yellow-200">{t('home.hero.badge')}</span>
          </motion.div>
          
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-6xl md:text-8xl font-bold mb-6"
          >
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-yellow-400 via-amber-500 to-yellow-600">
              {t('home.hero.title')}
            </span>
            <br />
            <span className="text-white">{t('home.hero.subtitle')}</span>
          </motion.h1>
          
          <motion.p 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-xl md:text-2xl text-gray-300 mb-12 max-w-3xl mx-auto"
          >
            {t('home.hero.description')}
          </motion.p>
          
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <button 
              onClick={() => handleDownload('https://apps.apple.com/us/app/confesz/id6541248711')}
              className="clickable group relative px-8 py-4 bg-gradient-to-r from-yellow-500 to-amber-500 rounded-full font-bold text-black text-lg overflow-hidden transition-transform hover:scale-105 shadow-lg shadow-yellow-500/30"
            >
              <span className="relative z-10">{t('home.hero.ios_button')}</span>
              <div className="absolute inset-0 bg-gradient-to-r from-yellow-600 to-amber-600 opacity-0 group-hover:opacity-100 transition-opacity" />
            </button>

            <button 
              onClick={() => handleDownload('https://play.google.com/store/apps/details?id=com.confesz.confeszapp')}
              className="clickable group relative px-8 py-4 bg-black/50 backdrop-blur-lg border border-yellow-500/40 rounded-full font-bold text-lg overflow-hidden transition-transform hover:scale-105 hover:border-yellow-500/60 shadow-lg shadow-yellow-500/10"
            >
              <span className="relative z-10 text-yellow-400">{t('home.hero.android_button')}</span>
              <div className="absolute inset-0 bg-yellow-500/10 opacity-0 group-hover:opacity-100 transition-opacity" />
            </button>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5, duration: 1 }}
            className="absolute bottom-10 left-1/2 transform -translate-x-1/2"
          >
            <ArrowDown className="w-6 h-6 animate-bounce text-yellow-400" />
          </motion.div>
        </div>
      </section>

      {/* What's New / Features Update Section */}
      <section className="relative z-10 py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-6xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-2 bg-yellow-500/20 rounded-full text-yellow-400 text-sm font-semibold mb-4 border border-yellow-500/30">
              {t('home.updates.badge')}
            </span>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-yellow-400 to-amber-500">
                {t('home.updates.title')}
              </span>
              <br />
              <span className="text-white">{t('home.updates.subtitle')}</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              {t('home.updates.description')}
            </p>
          </motion.div>

          {/* Feature Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {[
              {
                icon: MessageCircle,
                title: t('home.features.chat.title'),
                desc: t('home.features.chat.desc'),
                gradient: 'from-yellow-400 to-amber-400',
                stats: t('home.features.chat.stats')
              },
              {
                icon: Users2,
                title: t('home.features.friendships.title'),
                desc: t('home.features.friendships.desc'),
                gradient: 'from-amber-400 to-yellow-500',
                stats: t('home.features.friendships.stats')
              },
              {
                icon: Video,
                title: t('home.features.video_calls.title'),
                desc: t('home.features.video_calls.desc'),
                gradient: 'from-yellow-500 to-amber-500',
                stats: t('home.features.video_calls.stats')
              },
              {
                icon: Hash,
                title: t('home.features.societies.title'),
                desc: t('home.features.societies.desc'),
                gradient: 'from-amber-500 to-yellow-600',
                stats: t('home.features.societies.stats')
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -10 }}
                className="group relative"
              >
                <div className="absolute inset-0 bg-gradient-to-r opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl blur-xl"
                  style={{
                    background: `linear-gradient(135deg, ${feature.gradient.split(' ')[1]}, ${feature.gradient.split(' ')[3]})`
                  }}
                />
                <div className="relative bg-gray-900/80 backdrop-blur-sm border border-yellow-500/10 rounded-2xl p-6 h-full hover:border-yellow-500/30 transition-colors">
                  <div className="w-12 h-12 bg-gradient-to-br from-yellow-400/20 to-amber-400/20 rounded-xl flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-yellow-400" />
                  </div>
                  <h3 className="text-xl font-bold mb-2 text-yellow-200">{feature.title}</h3>
                  <p className="text-gray-400 text-sm mb-3">{feature.desc}</p>
                  <span className="text-xs text-yellow-400/80 font-semibold">{feature.stats}</span>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Humble brag section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="relative bg-gradient-to-r from-yellow-500/10 via-transparent to-amber-500/10 rounded-3xl p-8 border border-yellow-500/20"
          >
            <div className="absolute inset-0 bg-grid-pattern opacity-5" />
            <div className="relative flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center">
                  <HeartHandshake className="w-8 h-8 text-yellow-400" />
                </div>
                <div>
                  <h4 className="text-2xl font-bold text-white mb-1">{t('home.humble_brag.title')}</h4>
                  <p className="text-gray-400">{t('home.humble_brag.description')}</p>
                </div>
              </div>
              <div className="flex gap-6">
                <div className="text-center">
                  <div className="text-3xl font-bold text-yellow-400">3</div>
                  <div className="text-sm text-gray-400">{t('home.humble_brag.updates')}</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-yellow-400">4</div>
                  <div className="text-sm text-gray-400">{t('home.humble_brag.features')}</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold text-yellow-400">∞</div>
                  <div className="text-sm text-gray-400">{t('home.humble_brag.to_go')}</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="relative z-10 py-20 border-t border-yellow-500/10">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { number: '2M+', label: t('home.stats.downloads'), icon: Users },
              { number: '500K+', label: t('home.stats.daily_active'), icon: Zap },
              { number: '75+', label: t('home.stats.countries'), icon: Globe },
              { number: '100%', label: t('home.stats.free_always'), icon: Lock }
            ].map((stat, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <stat.icon className="w-8 h-8 mx-auto mb-4 text-yellow-400" />
                <div className="text-4xl font-bold mb-2 bg-clip-text text-transparent bg-gradient-to-r from-yellow-400 to-amber-500">
                  {stat.number}
                </div>
                <div className="text-gray-400">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Values Section */}
      <section className="relative z-10 py-20">
        <div className="max-w-6xl mx-auto px-4">
          <motion.h2 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-bold text-center mb-4"
          >
            {t('home.core_values.title')}{' '}
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-yellow-400 to-amber-500">
              {t('home.core_values.brand')}
            </span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xl text-gray-400 text-center mb-16 max-w-2xl mx-auto"
          >
            {t('home.core_values.subtitle')}
          </motion.p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                title: t('home.core_values.open_platform.title'),
                desc: t('home.core_values.open_platform.desc'),
                icon: Globe,
                gradient: 'from-yellow-500 to-amber-500'
              },
              {
                title: t('home.core_values.self_moderated.title'),
                desc: t('home.core_values.self_moderated.desc'),
                icon: Users,
                gradient: 'from-amber-500 to-yellow-600'
              },
              {
                title: t('home.core_values.temporary_posts.title'),
                desc: t('home.core_values.temporary_posts.desc'),
                icon: Zap,
                gradient: 'from-yellow-400 to-amber-400'
              },
              {
                title: t('home.core_values.simple_signup.title'),
                desc: t('home.core_values.simple_signup.desc'),
                icon: Lock,
                gradient: 'from-amber-400 to-yellow-500'
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -10 }}
                className="group relative"
              >
                <div className="absolute inset-0 bg-gradient-to-r opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl blur-xl"
                  style={{
                    background: `linear-gradient(135deg, ${feature.gradient.split(' ')[1]}, ${feature.gradient.split(' ')[3]})`
                  }}
                />
                <div className="relative bg-gray-900/50 backdrop-blur-lg border border-yellow-500/10 rounded-2xl p-8 h-full hover:border-yellow-500/30 transition-colors">
                  <feature.icon className="w-12 h-12 mb-4 text-yellow-400" />
                  <h3 className="text-xl font-bold mb-2 text-yellow-200">{feature.title}</h3>
                  <p className="text-gray-400">{feature.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Community Image Section */}
      <section className="relative z-10 py-20">
        <div className="max-w-6xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            className="relative rounded-2xl overflow-hidden group"
          >
            <img 
              src={communityImage} 
              alt="Confesz Community" 
              className="w-full h-auto transform group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-8">
              <h3 className="text-3xl font-bold mb-2 text-yellow-400">{t('home.community.title')}</h3>
              <p className="text-gray-300">{t('home.community.description')}</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="relative z-10 py-20 border-y border-yellow-500/10">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="relative"
          >
            <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
              <div className="w-16 h-16 bg-yellow-500/20 rounded-full blur-2xl" />
            </div>
            <p className="text-2xl md:text-3xl italic text-gray-300 mb-8">
              {t('home.testimonial.text')}
            </p>
            <div className="flex items-center justify-center gap-2">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="relative z-10 py-32">
        <div className="absolute inset-0 bg-gradient-to-r from-yellow-400/20 to-amber-300/20" />
        <div className="relative max-w-4xl mx-auto px-4 text-center">
          <motion.h2 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-bold mb-6"
          >
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-yellow-300 to-amber-400">
              {t('home.cta.title')}
            </span>
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xl text-gray-300 mb-10"
          >
            {t('home.cta.subtitle')}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Link to="/downloads">
              <button className="clickable px-8 py-4 bg-gradient-to-r from-yellow-400 to-amber-400 rounded-full font-bold text-black text-lg hover:shadow-lg hover:shadow-yellow-400/40 transition-all transform hover:scale-105">
                {t('home.cta.button')}
              </button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default Home;